import { useState, useEffect, useCallback } from 'react'
import { useEntities } from '../../hooks/useEntities'
import { useFormDraft } from '../../hooks/useFormDraft'
import { supabase } from '../../services/supabase'
import { Modal } from '../../components/Common/Modal'
import { FormField } from '../../components/Common/FormField'
import { Button } from '../../components/Common/Button'

const TABS = [
  { id: 'details', label: 'Details' },
  { id: 'address', label: 'Address' },
  { id: 'marital', label: 'Marital Details' },
  { id: 'contact', label: 'Contact' },
  { id: 'banking', label: 'Banking' },
  { id: 'attachments', label: 'Attachments' },
  { id: 'relatives', label: 'Relatives' },
]

export function ClientForm({ onSubmit, onClose, initialData, sidebarCollapsed, checkingDuplicates = false, isDarkMode = false }) {
  const [activeTab, setActiveTab] = useState('details')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [recordType, setRecordType] = useState('natural_person')
  const [generatingCode, setGeneratingCode] = useState(false)
  const isNew = !initialData?.id
  const { entities } = useEntities(1, 1000)
  const [advisorOptions, setAdvisorOptions] = useState([])

  useEffect(() => {
    // Fetch advisors that have a role named "Advisor"
    supabase
      .from('roles')
      .select('id')
      .ilike('name', 'advisor')
      .then(({ data: roleData }) => {
        if (!roleData?.length) {
          // Fallback: show all active advisors if no "Advisor" role exists
          supabase.from('advisors').select('id, full_name').eq('active', true).order('full_name')
            .then(({ data }) => setAdvisorOptions(data || []))
          return
        }
        const roleIds = roleData.map(r => r.id)
        supabase
          .from('advisor_roles')
          .select('advisor_id')
          .in('role_id', roleIds)
          .then(({ data: arData }) => {
            const advisorIds = [...new Set((arData || []).map(r => r.advisor_id))]
            if (!advisorIds.length) { setAdvisorOptions([]); return }
            supabase
              .from('advisors')
              .select('id, full_name')
              .in('id', advisorIds)
              .eq('active', true)
              .order('full_name')
              .then(({ data }) => setAdvisorOptions(data || []))
          })
      })
  }, [])

  const generateCustomerCode = useCallback(async (surname) => {
    const prefix = (surname || '').replace(/[^a-zA-Z]/g, '').substring(0, 4).toUpperCase()
    if (!prefix) return
    setGeneratingCode(true)
    try {
      const [{ data: clientCodes }, { data: entityCodes }] = await Promise.all([
        supabase.from('clients').select('customer_code').ilike('customer_code', `${prefix}%`),
        supabase.from('entities').select('customer_code').ilike('customer_code', `${prefix}%`),
      ])
      const all = [
        ...(clientCodes || []).map(c => c.customer_code),
        ...(entityCodes || []).map(e => e.customer_code),
      ].filter(Boolean)
      let maxNum = 0
      all.forEach(code => {
        const num = parseInt(code.slice(prefix.length), 10)
        if (!isNaN(num) && num > maxNum) maxNum = num
      })
      setClientData(prev => ({ ...prev, customer_code: prefix + String(maxNum + 1).padStart(3, '0') }))
    } finally {
      setGeneratingCode(false)
    }
  }, [])

  // Use form draft for auto-save to localStorage
  const defaultClientData = {
    surname: '',
    full_name: '',
    initials: '',
    preferred_name: '',
    title: '',
    gender: '',
    id_number: '',
    customer_code: '',
    tax_number: '',
    client_status: 'Prospective',
    date_of_birth: '',
    retirement_date: '',
    active: true,
    mobile_number: '',
    email_address: '',
    entity_id: '',
    advisor_id: '',
  }
  const draftKey = initialData?.id ? `clientForm_${initialData.id}` : 'clientForm_new'
  const { data: clientData, setData: setClientData, hasUnsavedChanges: clientHasChanges, clearDraft: clearClientDraft } = useFormDraft(draftKey, initialData || defaultClientData)

  // Auto-generate code for new records when surname is typed and code is still blank
  useEffect(() => {
    if (!isNew || clientData.customer_code) return
    const timer = setTimeout(() => {
      if (clientData.surname) generateCustomerCode(clientData.surname)
    }, 600)
    return () => clearTimeout(timer)
  }, [clientData.surname, clientData.customer_code, isNew, generateCustomerCode])

  const [addressData, setAddressData] = useState(initialData?.client_addresses?.[0] || {
    address_line_1: '',
    address_line_2: '',
    suburb: '',
    city: '',
    postal_code: '',
  })

  const [maritalData, setMaritalData] = useState(initialData?.client_marital_details?.[0] || {
    marital_status: '',
    marriage_date: '',
    antenuptial_contract: false,
  })

  const [bankingData, setBankingData] = useState(initialData?.client_banking_details?.[0] || {
    account_name: '',
    account_type: '',
    bank_name: '',
    branch_name: '',
    branch_code: '',
    account_number: '',
    active: true,
  })

  const [relativesData, setRelativesData] = useState(initialData?.client_relatives || [])

  const handleClientChange = (e) => {
    const { name, value, type, checked } = e.target
    setClientData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (name === 'customer_code' ? value.toUpperCase() : value)
    }))
  }

  const handleAddressChange = (e) => {
    const { name, value } = e.target
    setAddressData(prev => ({ ...prev, [name]: value }))
  }

  const handleMaritalChange = (e) => {
    const { name, value, type, checked } = e.target
    setMaritalData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const handleBankingChange = (e) => {
    const { name, value, type, checked } = e.target
    setBankingData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const addRelative = () => {
    setRelativesData([...relativesData, {
      full_name: '',
      relation: '',
      mobile: '',
      email: '',
      id_number: '',
      active: true,
      compliant: false,
    }])
  }

  const updateRelative = (idx, field, value) => {
    const updated = [...relativesData]
    updated[idx] = { ...updated[idx], [field]: value }
    setRelativesData(updated)
  }

  const removeRelative = (idx) => {
    setRelativesData(relativesData.filter((_, i) => i !== idx))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      // Validate customer code uniqueness across both tables
      if (clientData.customer_code) {
        const [{ data: existingClients }, { data: existingEntities }] = await Promise.all([
          supabase.from('clients').select('id').eq('customer_code', clientData.customer_code).neq('id', initialData?.id || '00000000-0000-0000-0000-000000000000'),
          supabase.from('entities').select('id').eq('customer_code', clientData.customer_code),
        ])
        if ((existingClients?.length || 0) + (existingEntities?.length || 0) > 0) {
          setError(`Customer code "${clientData.customer_code}" is already in use. Please choose a unique code.`)
          setLoading(false)
          return
        }
      }

      await onSubmit({
        clientData,
        addressData,
        maritalData,
        bankingData,
        relativesData,
        recordType,
      })
      // Clear saved draft after successful submission
      clearClientDraft()
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal isOpen={true} onClose={onClose} title="Client Details" size="full" sidebarCollapsed={sidebarCollapsed} isDarkMode={isDarkMode} managed>
      <form onSubmit={handleSubmit} className="flex-1 flex flex-col gap-4 pb-4 min-h-0">
        {error && (
          <div style={{ backgroundColor: isDarkMode ? '#7f1d1d' : '#fef2f2', borderColor: isDarkMode ? '#991b1b' : '#fecaca', color: isDarkMode ? '#fca5a5' : '#991b1b' }} className="border px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* IZULU Sync Badge */}
        {initialData?.izulu_profile_id && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm" style={{ backgroundColor: isDarkMode ? '#1e3a8a' : '#dbeafe', borderColor: isDarkMode ? '#3b82f6' : '#bfdbfe', borderWidth: '1px' }}>
            <span className="font-medium" style={{ color: isDarkMode ? '#bfdbfe' : '#1e40af' }}>🔗 Synced with IZULU Claims</span>
            {import.meta.env.VITE_IZULU_URL && (
              <a
                href={`${import.meta.env.VITE_IZULU_URL}/clients/${initialData.izulu_profile_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="underline ml-auto text-xs"
                style={{ color: isDarkMode ? '#93c5fd' : '#2563eb' }}
                onMouseEnter={(e) => e.target.style.color = isDarkMode ? '#bfdbfe' : '#1d4ed8'}
                onMouseLeave={(e) => e.target.style.color = isDarkMode ? '#93c5fd' : '#2563eb'}
              >
                View in IZULU →
              </a>
            )}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 overflow-x-auto" style={{ borderBottom: `1px solid ${isDarkMode ? '#475569' : '#e5e7eb'}` }}>
          {TABS.map(tab => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 font-semibold text-sm transition whitespace-nowrap`}
              style={{
                borderBottom: activeTab === tab.id ? '2px solid #F5A623' : 'none',
                color: activeTab === tab.id ? '#F5A623' : (isDarkMode ? '#cbd5e1' : '#666666')
              }}
              onMouseEnter={(e) => e.target.style.color = '#F5A623'}
              onMouseLeave={(e) => e.target.style.color = activeTab === tab.id ? '#F5A623' : (isDarkMode ? '#cbd5e1' : '#666666')}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="flex-1 min-h-0 overflow-y-auto space-y-4">
          {/* Details Tab */}
          {activeTab === 'details' && (
            <div className="space-y-4">
              <FormField
                isDarkMode={isDarkMode}
                label="Surname"
                name="surname"
                value={clientData.surname}
                onChange={handleClientChange}
                required
              />
              <FormField
                isDarkMode={isDarkMode}
                label="Full Name"
                name="full_name"
                value={clientData.full_name}
                onChange={handleClientChange}
              />
              <div className="grid grid-cols-2 gap-4">
                <FormField
                isDarkMode={isDarkMode}
                  label="Initials"
                  name="initials"
                  value={clientData.initials}
                  onChange={handleClientChange}
                />
                <FormField
                isDarkMode={isDarkMode}
                  label="Preferred Name"
                  name="preferred_name"
                  value={clientData.preferred_name}
                  onChange={handleClientChange}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                isDarkMode={isDarkMode}
                  label="Title"
                  name="title"
                  value={clientData.title}
                  onChange={handleClientChange}
                  placeholder="Mr., Mrs., Dr., etc."
                />
                <FormField
                isDarkMode={isDarkMode}
                  label="Gender"
                  name="gender"
                  value={clientData.gender}
                  onChange={handleClientChange}
                />
              </div>
              <FormField
                isDarkMode={isDarkMode}
                label="ID Number"
                name="id_number"
                value={clientData.id_number}
                onChange={handleClientChange}
              />
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: isDarkMode ? '#e2e8f0' : '#333333' }}>
                  Customer Code
                </label>
                <div className="flex gap-2">
                  <input
                    name="customer_code"
                    value={clientData.customer_code}
                    onChange={handleClientChange}
                    placeholder="e.g. SMIT001"
                    className="input-field flex-1"
                    style={{ fontFamily: 'monospace', letterSpacing: '0.05em' }}
                  />
                  <button
                    type="button"
                    onClick={() => generateCustomerCode(clientData.surname)}
                    disabled={generatingCode || !clientData.surname}
                    title="Auto-generate from surname"
                    className="px-3 py-2 rounded text-xs font-semibold transition flex-shrink-0 disabled:opacity-40"
                    style={{
                      backgroundColor: isDarkMode ? '#334155' : '#f1f5f9',
                      color: isDarkMode ? '#94a3b8' : '#64748b',
                      border: `1px solid ${isDarkMode ? '#475569' : '#d1d5db'}`,
                    }}
                  >
                    {generatingCode ? '…' : '↺ Auto'}
                  </button>
                </div>
                <p className="text-xs mt-1" style={{ color: isDarkMode ? '#64748b' : '#9ca3af' }}>
                  Used to link policies. Must be unique across all clients and entities.
                </p>
              </div>
              <FormField
                isDarkMode={isDarkMode}
                label="Tax Number"
                name="tax_number"
                value={clientData.tax_number}
                onChange={handleClientChange}
              />
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="client_status" className="block text-sm font-semibold mb-2" style={{ color: isDarkMode ? '#e2e8f0' : '#333333' }}>
                    Client Status
                  </label>
                  <select
                    id="client_status"
                    name="client_status"
                    value={clientData.client_status}
                    onChange={handleClientChange}
                    className="input-field"
                  >
                    <option>Prospective</option>
                    <option>Active</option>
                    <option>Inactive</option>
                  </select>
                </div>
                <FormField
                isDarkMode={isDarkMode}
                  label="Date of Birth"
                  name="date_of_birth"
                  type="date"
                  value={clientData.date_of_birth}
                  onChange={handleClientChange}
                />
              </div>
              <FormField
                isDarkMode={isDarkMode}
                label="Retirement Date"
                name="retirement_date"
                type="date"
                value={clientData.retirement_date}
                onChange={handleClientChange}
              />
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: isDarkMode ? '#e2e8f0' : '#333333' }}>
                  Record Type
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      id="recordType_natural"
                      name="recordType"
                      type="radio"
                      value="natural_person"
                      checked={recordType === 'natural_person'}
                      onChange={(e) => setRecordType(e.target.value)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm" style={{ color: isDarkMode ? '#cbd5e1' : '#333333' }}>Natural Person</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      id="recordType_entity"
                      name="recordType"
                      type="radio"
                      value="entity_business"
                      checked={recordType === 'entity_business'}
                      onChange={(e) => setRecordType(e.target.value)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm" style={{ color: isDarkMode ? '#cbd5e1' : '#333333' }}>Entity / Business</span>
                  </label>
                </div>
              </div>
              {recordType === 'entity_business' && (
                <div className="p-3 rounded bg-amber-50 border border-amber-200" style={{ backgroundColor: isDarkMode ? '#78350f' : '#fffbeb', borderColor: isDarkMode ? '#b45309' : '#fbbf24' }}>
                  <p className="text-xs font-semibold" style={{ color: isDarkMode ? '#fcd34d' : '#92400e' }}>
                    ⚠️ Moving to Entities
                  </p>
                  <p className="text-xs mt-1" style={{ color: isDarkMode ? '#fed7aa' : '#b45309' }}>
                    Saving will move this record to the Entities section and re-link any associated policies. Addresses and banking details will need to be re-entered in the Entity form.
                  </p>
                </div>
              )}
              <div>
                <label htmlFor="advisor_id" className="block text-sm font-semibold mb-2" style={{ color: isDarkMode ? '#e2e8f0' : '#333333' }}>
                  Assigned Advisor
                </label>
                <select
                  id="advisor_id"
                  name="advisor_id"
                  value={clientData.advisor_id}
                  onChange={handleClientChange}
                  className="input-field"
                >
                  <option value="">Select Advisor</option>
                  {advisorOptions.map(advisor => (
                    <option key={advisor.id} value={advisor.id}>
                      {advisor.full_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Address Tab */}
          {activeTab === 'address' && (
            <div className="space-y-4">
              <FormField
                isDarkMode={isDarkMode}
                label="Address Line 1"
                name="address_line_1"
                value={addressData.address_line_1}
                onChange={handleAddressChange}
              />
              <FormField
                isDarkMode={isDarkMode}
                label="Address Line 2"
                name="address_line_2"
                value={addressData.address_line_2}
                onChange={handleAddressChange}
              />
              <div className="grid grid-cols-2 gap-4">
                <FormField
                isDarkMode={isDarkMode}
                  label="Suburb"
                  name="suburb"
                  value={addressData.suburb}
                  onChange={handleAddressChange}
                />
                <FormField
                isDarkMode={isDarkMode}
                  label="City"
                  name="city"
                  value={addressData.city}
                  onChange={handleAddressChange}
                />
              </div>
              <FormField
                isDarkMode={isDarkMode}
                label="Postal Code"
                name="postal_code"
                value={addressData.postal_code}
                onChange={handleAddressChange}
              />
            </div>
          )}

          {/* Marital Details Tab */}
          {activeTab === 'marital' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: isDarkMode ? '#e2e8f0' : '#333333' }}>
                  Marital Status
                </label>
                <select
                  name="marital_status"
                  value={maritalData.marital_status}
                  onChange={handleMaritalChange}
                  className="input-field"
                >
                  <option></option>
                  <option>Single</option>
                  <option>Married</option>
                  <option>Divorced</option>
                  <option>Widowed</option>
                  <option>Domestic Partnership</option>
                </select>
              </div>
              <FormField
                isDarkMode={isDarkMode}
                label="Marriage Date"
                name="marriage_date"
                type="date"
                value={maritalData.marriage_date}
                onChange={handleMaritalChange}
              />
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="antenuptial"
                  name="antenuptial_contract"
                  checked={maritalData.antenuptial_contract}
                  onChange={handleMaritalChange}
                  className="w-4 h-4"
                />
                <label htmlFor="antenuptial" className="text-sm font-medium" style={{ color: isDarkMode ? '#e2e8f0' : '#333333' }}>
                  Antenuptial Contract
                </label>
              </div>
            </div>
          )}

          {/* Contact Tab */}
          {activeTab === 'contact' && (
            <div className="space-y-4">
              <FormField
                isDarkMode={isDarkMode}
                label="Mobile Number"
                name="mobile_number"
                value={clientData.mobile_number}
                onChange={handleClientChange}
              />
              <FormField
                isDarkMode={isDarkMode}
                label="Email Address"
                name="email_address"
                type="email"
                value={clientData.email_address}
                onChange={handleClientChange}
              />
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: isDarkMode ? '#e2e8f0' : '#333333' }}>
                  Entity
                </label>
                <select
                  name="entity_id"
                  value={clientData.entity_id}
                  onChange={handleClientChange}
                  className="input-field"
                >
                  <option value="">Select Entity</option>
                  {entities.map(entity => (
                    <option key={entity.id} value={entity.id}>
                      {entity.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Banking Tab */}
          {activeTab === 'banking' && (
            <div className="space-y-4">
              <FormField
                isDarkMode={isDarkMode}
                label="Account Name"
                name="account_name"
                value={bankingData.account_name}
                onChange={handleBankingChange}
              />
              <FormField
                isDarkMode={isDarkMode}
                label="Account Type"
                name="account_type"
                value={bankingData.account_type}
                onChange={handleBankingChange}
                placeholder="Savings, Checking, etc."
              />
              <FormField
                isDarkMode={isDarkMode}
                label="Bank Name"
                name="bank_name"
                value={bankingData.bank_name}
                onChange={handleBankingChange}
              />
              <div className="grid grid-cols-2 gap-4">
                <FormField
                isDarkMode={isDarkMode}
                  label="Branch Name"
                  name="branch_name"
                  value={bankingData.branch_name}
                  onChange={handleBankingChange}
                />
                <FormField
                isDarkMode={isDarkMode}
                  label="Branch Code"
                  name="branch_code"
                  value={bankingData.branch_code}
                  onChange={handleBankingChange}
                />
              </div>
              <FormField
                isDarkMode={isDarkMode}
                label="Account Number"
                name="account_number"
                value={bankingData.account_number}
                onChange={handleBankingChange}
              />
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="banking_active"
                  name="active"
                  checked={bankingData.active}
                  onChange={handleBankingChange}
                  className="w-4 h-4"
                />
                <label htmlFor="banking_active" className="text-sm font-medium" style={{ color: isDarkMode ? '#e2e8f0' : '#333333' }}>
                  Active
                </label>
              </div>
            </div>
          )}

          {/* Attachments Tab */}
          {activeTab === 'attachments' && (
            <div className="space-y-4 text-center" style={{ color: isDarkMode ? '#cbd5e1' : '#666666' }}>
              <p>Document upload functionality coming soon</p>
              <p className="text-xs">Supported: ID Document, Proof of Address, SARS VAT Clearance</p>
            </div>
          )}

          {/* Relatives Tab */}
          {activeTab === 'relatives' && (
            <div className="space-y-4">
              {relativesData.map((relative, idx) => (
                <div key={idx} style={{ borderColor: isDarkMode ? '#475569' : '#e5e7eb' }} className="p-4 border rounded space-y-3">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-semibold text-sm" style={{ color: isDarkMode ? '#cbd5e1' : '#333333' }}>
                      Relative {idx + 1}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeRelative(idx)}
                      className="text-red-600 hover:text-red-800 text-sm"
                    >
                      ✕ Remove
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      id={`relative-${idx}-full_name`}
                      name={`relative-${idx}-full_name`}
                      type="text"
                      placeholder="Full Name"
                      value={relative.full_name}
                      onChange={(e) => updateRelative(idx, 'full_name', e.target.value)}
                      className="input-field text-sm py-1 px-3"
                    />
                    <input
                      id={`relative-${idx}-relation`}
                      name={`relative-${idx}-relation`}
                      type="text"
                      placeholder="Relation"
                      value={relative.relation}
                      onChange={(e) => updateRelative(idx, 'relation', e.target.value)}
                      className="input-field text-sm py-1 px-3"
                    />
                    <input
                      id={`relative-${idx}-mobile`}
                      name={`relative-${idx}-mobile`}
                      type="tel"
                      placeholder="Mobile"
                      value={relative.mobile}
                      onChange={(e) => updateRelative(idx, 'mobile', e.target.value)}
                      className="input-field text-sm py-1 px-3"
                    />
                    <input
                      id={`relative-${idx}-email`}
                      name={`relative-${idx}-email`}
                      type="email"
                      placeholder="Email"
                      value={relative.email}
                      onChange={(e) => updateRelative(idx, 'email', e.target.value)}
                      className="input-field text-sm py-1 px-3"
                    />
                    <input
                      id={`relative-${idx}-id_number`}
                      name={`relative-${idx}-id_number`}
                      type="text"
                      placeholder="ID Number"
                      value={relative.id_number}
                      onChange={(e) => updateRelative(idx, 'id_number', e.target.value)}
                      className="input-field text-sm py-1 px-3"
                    />
                  </div>
                  <div className="flex gap-3">
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={relative.active}
                        onChange={(e) => updateRelative(idx, 'active', e.target.checked)}
                        className="w-3 h-3"
                      />
                      Active
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={relative.compliant}
                        onChange={(e) => updateRelative(idx, 'compliant', e.target.checked)}
                        className="w-3 h-3"
                      />
                      Compliant
                    </label>
                  </div>
                </div>
              ))}
              <Button
                variant="secondary"
                type="button"
                onClick={addRelative}
                className="w-full"
              >
                + Add Relative
              </Button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ borderTop: `1px solid ${isDarkMode ? '#475569' : '#e5e7eb'}` }} className="flex gap-2 justify-between items-center pt-4 flex-shrink-0">
          <div className="flex items-center gap-2">
            {clientHasChanges && (
              <>
                <span className="text-xs font-semibold" style={{ color: '#F5A623' }}>
                  ● Unsaved changes
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  type="button"
                  onClick={() => { clearClientDraft(); setActiveTab('details') }}
                  title="Clear unsaved draft and reset form"
                  className="text-xs"
                >
                  Clear Draft
                </Button>
              </>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={onClose} type="button">
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={loading || checkingDuplicates}>
              {checkingDuplicates ? 'Checking IZULU...' : loading ? 'Saving...' : 'Save Client'}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  )
}
